import { FC, ReactNode, useState, useMemo } from "react";

import { WindowContext, WindowContextProps, useWindow } from "../contexts/WindowContext";
import Window from '../components/Window';

const defaultState: WindowContextProps = {
    component: undefined,
    props: {},
    isOpened: false
};

const WindowManager: FC = () => {
    const { isOpened, component: Component, props, closeWindow, customImage, statusBarColor, darkStatusBarText } = useWindow();

    return (
        <>
            <Window 
                {...props} 
                customImage={customImage || {}} 
                isOpened={isOpened} 
                onClose={closeWindow}
                statusBarColor={statusBarColor}
                darkStatusBarText={darkStatusBarText}
            >
                {Component && <Component />}
            </Window>
        </>
    );
};

interface WindowProviderProps {
    children: ReactNode;
}

export const WindowProvider: FC<WindowProviderProps> = ({ children }) => {
    const [openState, setOpenState] = useState(false);
    const [state, setState] = useState<WindowContextProps>(defaultState);

    const actions = useMemo(
        () => ({
            open({ component = undefined, isOpened = false, statusBarColor, darkStatusBarText, ...props }: WindowContextProps) {
                setState({
                    component,
                    props,
                    isOpened,
                    statusBarColor,
                    darkStatusBarText,
                });
                setOpenState(true);
            },
            close() {
                setOpenState(false);
            },
        }),
        [],
    );
    return (
        <WindowContext.Provider
            value={{
                isOpened: openState,
                component: state.component,
                props: state.props,
                openWindow: (args: WindowContextProps) => actions.open(args),
                closeWindow: actions.close,
                customImage: state.customImage,
                statusBarColor: state.statusBarColor,
                darkStatusBarText: state.darkStatusBarText,
            }}
        >
            <WindowManager />
            {children}
        </WindowContext.Provider>
    );
};